import React, { FC, useMemo, useState } from 'react';
import { isAfter, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { useModalStore } from 'stores/modal';
import Contact from 'core/models/contact';
import MultiActionsButton from 'components/MultiActionsButton';
import Plus from 'images/icons/Plus.svg';
import Button from 'components/Button';
import { SButton, SSubCard, SWrapper } from './styles';
import EventType from 'core/enums/eventType';
import TabButtonsGroup from 'components/TabButtonsGroup';
import TabButton from 'components/TabButton';
import { useEventsByContactIdSortedByDate } from 'core/api/events';
import { useTasksByContactIdSortedByDeadline } from 'core/api/tasks';
import { computeActivitiesFromTasksAndEvents } from 'core/useCases/activities/utils';
import InProgressActivitiesSummary from 'components/Activity/InProgressActivitiesSummary';
import FinishedActivitiesSummary from 'components/Activity/FinishedActivitiesSummary';
import { SBoldPrimaryText } from 'styles/components';
import CardWithTitle from 'components/CardWithTitle';
import useResponsive from 'hooks/responsive';

type ActivitiesProps = {
    contact: Contact | null;
};

const Activities: FC<ActivitiesProps> = ({ contact }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    const { isMobile } = useResponsive();
    const [inProgressOptionSelected, setInProgressOptionSelected] = useState<boolean>(true);
    const contactId = contact?.id;
    const createdDate = contact?.createdDate || new Date();
    const creationDetails = {
        date: createdDate,
        label: t('contact.contact-added'),
    };

    const openTaskModal = () => {
        openModal({
            defaultValues: {
                contact,
            },
            disabledFields: [DisabledField.ContactId],
            title: t('task-list.add-task'),
            type: ModalType.Task,
        });
    };

    const openInterviewModal = () => {
        openModal({
            defaultValues: {
                contact,
                type: EventType.Interview,
            },
            disabledFields: [DisabledField.ContactId],
            title: t('activities.add-rendez-vous'),
            type: ModalType.Event,
        });
    };

    const openApplicationModal = () => {
        openModal({
            defaultValues: {
                contacts: contact ? [contact] : [],
            },
            title: t('application.add-application'),
            type: ModalType.Application,
        });
    };

    const { data: eventsData } = useEventsByContactIdSortedByDate(contactId);
    const events = useMemo(() => eventsData ?? [], [eventsData]);
    const { data: tasksData } = useTasksByContactIdSortedByDeadline(contactId);
    const tasks = useMemo(() => tasksData ?? [], [tasksData]);

    const { finishedActivities, inProgressActivities } = computeActivitiesFromTasksAndEvents(tasks, events, t);

    finishedActivities.sort((a, b): number => {
        if (isBefore(new Date(a.date), new Date(b.date))) {
            return 1;
        }
        return -1;
    });

    inProgressActivities.sort((a, b): number => {
        if (isAfter(new Date(a.date), new Date(b.date))) {
            return 1;
        }
        return -1;
    });

    const Card = () => (
        <>
            <SSubCard>
                <TabButtonsGroup>
                    <TabButton
                        onClick={() => setInProgressOptionSelected(true)}
                        variant={inProgressOptionSelected ? 'plain' : 'text'}
                    >
                        {t('common.in-progress')}
                    </TabButton>
                    <TabButton
                        onClick={() => setInProgressOptionSelected(false)}
                        variant={inProgressOptionSelected ? 'text' : 'plain'}
                    >
                        {t('common.finished')}
                    </TabButton>
                </TabButtonsGroup>
                {inProgressOptionSelected ? (
                    <InProgressActivitiesSummary activities={inProgressActivities} />
                ) : (
                    <FinishedActivitiesSummary activities={finishedActivities} creationDetails={creationDetails} />
                )}
            </SSubCard>
            <SButton>
                <MultiActionsButton
                    parentButtonContent={
                        <>
                            <img alt="" src={Plus} width="20" />
                            {t('common.add')}
                        </>
                    }
                >
                    <Button onClick={openTaskModal} variant="popinText">
                        {t('task.add')}
                    </Button>
                    <Button onClick={openInterviewModal} variant="popinText">
                        {t('interview.add-rendez-vous')}
                    </Button>
                    <Button onClick={openApplicationModal} variant="popinText">
                        {t('application.add-application')}
                    </Button>
                </MultiActionsButton>
            </SButton>
        </>
    );
    return isMobile ? (
        <>
            <SBoldPrimaryText>{t('common.activities-details')}</SBoldPrimaryText>
            <Card />
        </>
    ) : (
        <SWrapper>
            <CardWithTitle label={t('common.activities')}>
                <Card />
            </CardWithTitle>
        </SWrapper>
    );
};

export default Activities;
